import type { StateCreator } from 'zustand/vanilla';

import type { IPRule, TimeRule } from '@/features/sharing-profile/types/sharing-profile.interface';

import type { SharingStore } from '../../store';
import { initialPoliciesState } from './initial-state';

export interface SharingPoliciesAction {
  /**
   * Create or fully replace the time rule for a given day (e.g. "Mon").
   * Used both to initialize a day entry and to overwrite it wholesale.
   */
  setTimeRule: (day: string, rule: TimeRule) => void;
  /**
   * Merge a partial patch into an existing day's time rule (e.g. toggling
   * `enabled` or updating `start`/`end`) without touching the other fields.
   */
  updateTimeRule: (day: string, patch: Partial<TimeRule>) => void;
  /**
   * Remove a day's time rule entirely.
   */
  removeTimeRule: (day: string) => void;
  /**
   * Create or fully replace an IP rule by its generated rule ID.
   */
  setIpRule: (id: string, rule: IPRule) => void;
  /**
   * Remove an IP rule by its generated rule ID.
   */
  removeIpRule: (id: string) => void;
  /**
   * Toggle whether timezone/time-of-day restrictions are enabled for the profile.
   */
  setTimezoneEnabled: (enabled: boolean) => void;
  /**
   * Toggle whether IP restrictions are enabled for the profile.
   */
  setIpRestrictionsEnabled: (enabled: boolean) => void;
  /**
   * Reset all policy state (time rules, IP rules, and both restriction flags)
   * back to their initial values.
   */
  resetPolicies: () => void;
}

export const createPoliciesSlice: StateCreator<SharingStore, [['zustand/devtools', never]], [], SharingPoliciesAction> = (set) => ({
  setTimeRule: (day, rule) => {
    set((state) => ({
      timeRules: {
        ...state.timeRules,
        [day]: rule,
      },
    }));
  },

  updateTimeRule: (day, patch) => {
    set((state) => ({
      timeRules: {
        ...state.timeRules,
        [day]: {
          ...(state.timeRules[day] ?? {}),
          ...patch,
        },
      },
    }));
  },

  removeTimeRule: (day) => {
    set((state) => {
      const timeRules = { ...state.timeRules };
      delete timeRules[day];
      return { timeRules };
    });
  },

  setIpRule: (id, rule) => {
    set((state) => ({
      ipRules: {
        ...state.ipRules,
        [id]: rule,
      },
    }));
  },

  removeIpRule: (id) => {
    set((state) => {
      const ipRules = { ...state.ipRules };
      delete ipRules[id];
      return { ipRules };
    });
  },

  setTimezoneEnabled: (enabled) => {
    set({ timezone: enabled });
  },

  setIpRestrictionsEnabled: (enabled) => {
    set({ ipRestrictions: enabled });
  },

  resetPolicies: () => {
    set({ ...initialPoliciesState });
  },
});
